/**
 * Custom hook for managing metric selection in dynamic stacked charts
 * Enforces minimum 1 metric selection and tracks available metrics
 */

import { useState, useMemo, useCallback } from 'react';
import type {
  MetricType,
  ChartMetricConfig,
  ActivityStreamResponse,
} from '../../../types';
import {
  TrendingUpOutlined,
  FavoriteOutlined,
  TerrainOutlined,
  DirectionsRunOutlined,
  BoltOutlined,
  SpeedOutlined,
} from '@mui/icons-material';
import { getMetricColor, isMetricAvailable } from '../../../utils/chartUtils';

/**
 * Get metric configurations with labels and units
 * Cadence configuration is dynamic based on sport type
 */
const getMetricConfigs = (sportType?: string): Record<MetricType, Omit<ChartMetricConfig, 'available'>> => {
  const isRun = sportType?.toLowerCase().includes('run');

  return {
    pace: {
      id: 'pace',
      label: 'Pace',
      color: getMetricColor('pace'),
      unit: 'min/km',
      dataKey: 'velocity_smooth',
      icon: TrendingUpOutlined,
      invertAxis: true, // Pace should be inverted (lower is better)
      formatValue: (value: number) => {
        const minutes = Math.floor(value);
        const seconds = Math.floor((value - minutes) * 60);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
      },
    },
    gradeAdjustedPace: {
      id: 'gradeAdjustedPace',
      label: 'Grade Adj. Pace',
      color: getMetricColor('gradeAdjustedPace'),
      unit: 'min/km',
      dataKey: 'grade_adjusted_speed',
      icon: TerrainOutlined,
      invertAxis: true, // Pace should be inverted (lower is better)
      formatValue: (value: number) => {
        const minutes = Math.floor(value);
        const seconds = Math.floor((value - minutes) * 60);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
      },
    },
    heartRate: {
      id: 'heartRate',
      label: 'Heart Rate',
      color: getMetricColor('heartRate'),
      unit: 'bpm',
      dataKey: 'heartrate',
      icon: FavoriteOutlined,
      formatValue: (value: number) => `${Math.round(value)}`,
    },
    altitude: {
      id: 'altitude',
      label: 'Altitude',
      color: getMetricColor('altitude'),
      unit: 'm',
      dataKey: 'altitude',
      icon: TerrainOutlined,
      formatValue: (value: number) => `${Math.round(value)}`,
    },
    cadence: {
      id: 'cadence',
      label: 'Cadence',
      color: getMetricColor('cadence'),
      unit: isRun ? 'spm' : 'rpm', // spm for runs, rpm for bikes
      dataKey: 'cadence',
      icon: DirectionsRunOutlined,
      formatValue: (value: number) => `${Math.round(value)}`,
    },
    power: {
      id: 'power',
      label: 'Power',
      color: getMetricColor('power'),
      unit: 'W',
      dataKey: 'watts',
      icon: BoltOutlined,
      disabled: true, // Often not available
      formatValue: (value: number) => `${Math.round(value)}`,
    },
    speed: {
      id: 'speed',
      label: 'Speed',
      color: getMetricColor('speed'),
      unit: 'km/h',
      dataKey: 'velocity_smooth',
      icon: SpeedOutlined,
      formatValue: (value: number) => `${(value * 3.6).toFixed(1)}`,
    },
  };
};

export interface UseMetricSelectionReturn {
  selectedMetrics: MetricType[];
  availableMetrics: ChartMetricConfig[];
  toggleMetric: (metric: MetricType) => void;
  setMetrics: (metrics: MetricType[]) => void;
  isMetricSelected: (metric: MetricType) => boolean;
  canDeselectMetric: (metric: MetricType) => boolean;
  selectAll: () => void;
  resetToDefault: () => void;
}

/**
 * Hook for managing metric selection state
 */
export function useMetricSelection(
  streams: ActivityStreamResponse | null,
  defaultMetrics: MetricType[] = ['pace'],
  sportType?: string
): UseMetricSelectionReturn {
  // Initialize with default metrics
  const [selectedMetrics, setSelectedMetrics] = useState<MetricType[]>(defaultMetrics);

  // Calculate available metrics based on stream data
  const availableMetrics = useMemo<ChartMetricConfig[]>(() => {
    const configs = getMetricConfigs(sportType);
    return Object.values(configs)
      .map(config => ({
        ...config,
        available: isMetricAvailable(streams, config.id),
      }))
      .sort((a, b) => {
        // Put unavailable metrics first (available: false comes before available: true)
        if (a.available === b.available) return 0;
        return a.available ? 1 : -1;
      });
  }, [streams, sportType]);

  // Check if a metric is currently selected
  const isMetricSelected = useCallback(
    (metric: MetricType): boolean => {
      return selectedMetrics.includes(metric);
    },
    [selectedMetrics]
  );

  // Check if a metric can be deselected (must have at least 1 selected)
  const canDeselectMetric = useCallback(
    (metric: MetricType): boolean => {
      return selectedMetrics.length > 1 || !isMetricSelected(metric);
    },
    [selectedMetrics, isMetricSelected]
  );

  // Toggle a metric on/off
  const toggleMetric = useCallback(
    (metric: MetricType) => {
      setSelectedMetrics(prev => {
        const isCurrentlySelected = prev.includes(metric);

        // Prevent deselecting the last metric
        if (isCurrentlySelected && prev.length === 1) {
          return prev;
        }

        if (isCurrentlySelected) {
          return prev.filter(m => m !== metric);
        } else {
          return [...prev, metric];
        }
      });
    },
    []
  );

  // Set metrics directly (enforces minimum 1 metric)
  const setMetrics = useCallback((metrics: MetricType[]) => {
    if (metrics.length === 0) {
      console.warn('Cannot set empty metrics array. At least one metric must be selected.');
      return;
    }
    setSelectedMetrics(metrics);
  }, []);

  // Select all available metrics
  const selectAll = useCallback(() => {
    const allAvailable = availableMetrics
      .filter(m => m.available)
      .map(m => m.id);

    if (allAvailable.length > 0) {
      setSelectedMetrics(allAvailable);
    }
  }, [availableMetrics]);

  // Reset to default metrics
  const resetToDefault = useCallback(() => {
    const defaultAvailable = defaultMetrics.filter(metric =>
      availableMetrics.find(m => m.id === metric && m.available)
    );

    setSelectedMetrics(defaultAvailable.length > 0 ? defaultAvailable : [defaultMetrics[0]]);
  }, [defaultMetrics, availableMetrics]);

  return {
    selectedMetrics,
    availableMetrics,
    toggleMetric,
    setMetrics,
    isMetricSelected,
    canDeselectMetric,
    selectAll,
    resetToDefault,
  };
}
